// ─────────────────────────────────────────────
//  doctorSteps — WHEN do we come down in doctors, and when do we go back up?
//
//  `buildDoctorStaffing` answers the same question at four fixed hours
//  (3/5/7/9 PM). That is the right maths sampled at the wrong times: rooms do
//  not finish on the hour, and "SHORT 2 AT 3 PM" cannot tell a board manager
//  that the third doctor is free at 12:30. This walks the day's ACTUAL
//  crossings instead — every minute the requirement changes, and nowhere else.
//
//  ⚠️ THE STEP IS LABELLED WITH THE REAL ROOM COUNT, NOT THE RATIO THRESHOLD.
//  Five rooms finishing together takes 16 → 11, which still needs 3 doctors.
//  Printing "12 rooms" there because 12 is where the 3-doctor band starts would
//  be a fabricated number on a screen people plan staffing from.
//
//  ⚠️ A STEP DOWN IS PROVISIONAL UNTIL THE END OF THE OR DAY. Coming down to
//  one doctor at 16:00 is not permission to send the second one home if a 17:00
//  add-on puts the requirement back to two. Every step therefore carries
//  `holdExtra` — how many doctors it must NOT release — computed by looking at
//  every later step, not just the next one. Releasing on the strength of the
//  16:00 row alone is the actual failure mode this box exists to prevent.
// ─────────────────────────────────────────────
import { STAFF_BUFFER_MIN, ROOMS_PER_DOCTOR, hmToMinutes, minutesToHm, to12h } from "./timeline";
import { roomsRunningAt, requiredDoctors, prepareDoctors, doctorsAvailableAt } from "./doctorCoverage";

// The OR day runs 07:00 → 07:00, so a case finishing at 02:00 is minute 1560,
// not minute 120. Anything projected past the far end is not today's problem.
export const OR_DAY_END_MIN = 31 * 60;

// How close a step has to be before the box asks you to act on it.
export const STEP_WARN_MIN = 15;

// Every minute at which the room count can change: a case starting, or a case
// ending plus the staffing tail. Between two adjacent candidates the count is
// constant, so these are the only minutes worth evaluating.
function candidateMinutes(placed, bufferMin, dayEndMin) {
  const times = new Set();
  for (const p of Array.isArray(placed) ? placed : []) {
    if (!p) continue;
    const start = p.startMin;
    const end = p.endMin;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
    if (start <= dayEndMin) times.add(start);
    const off = end + bufferMin;
    if (off <= dayEndMin) times.add(off);
  }
  return [...times].sort((a, b) => a - b);
}

/**
 * @param placed  buildTimeline's flat per-case list
 *                { roomId, startMin, endMin, solo, attendingId, attendingName }
 * @param rosterDoctors  the day's supervising doctors (post `attachLocumLeaves`)
 * @param opts    { ratio, nowMin, warnMin, bufferMin, dayEndMin }
 *
 * @returns { now, steps }
 *   now   — the live reading, or NULL when there is no clock (a board that is
 *           not today). Never a zero: "no doctors needed" and "we are not
 *           looking at today" must not render the same.
 *   steps — every future change in the requirement, each carrying the room
 *           count that caused it and whether an extra doctor must be held.
 */
export function doctorSteps(placed, rosterDoctors, opts) {
  const o = opts || {};
  const ratio = Number.isFinite(Number(o.ratio)) && Number(o.ratio) > 0 ? Number(o.ratio) : ROOMS_PER_DOCTOR;
  const nowMin = Number.isFinite(o.nowMin) ? o.nowMin : null;
  const warnMin = Number.isFinite(o.warnMin) ? o.warnMin : STEP_WARN_MIN;
  const bufferMin = Number.isFinite(o.bufferMin) ? o.bufferMin : STAFF_BUFFER_MIN;
  const dayEndMin = Number.isFinite(o.dayEndMin) ? o.dayEndMin : OR_DAY_END_MIN;
  // Rooms whose case is no longer happening (cancelled). Optional: a caller that
  // does not pass it gets exactly the old behaviour.
  const ended = o.endedRoomIds instanceof Set ? o.endedRoomIds : null;

  const prepared = prepareDoctors(rosterDoctors, { hmToMin: hmToMinutes });

  // One reading of the whole picture at a single minute.
  const readAt = (T) => {
    const at = roomsRunningAt(placed, T, bufferMin, ended);
    const rooms = at.rooms.size;
    const soloRooms = at.soloRooms.size;
    const supervisionRooms = Math.max(0, rooms - soloRooms);
    const required = requiredDoctors(supervisionRooms, ratio);
    const { available, doctorList, soloList } = doctorsAvailableAt(prepared, T, at);
    return {
      rooms,
      soloRooms,
      supervisionRooms,
      required,
      available,
      doctorList,
      soloList,
      delta: available - required,
      short: Math.max(0, required - available),
    };
  };

  const candidates = candidateMinutes(placed, bufferMin, dayEndMin);

  // ⚠️ SEEDED FROM THE FIRST CANDIDATE, NOT FROM ZERO. Every room starting in
  // the morning is a change from "no doctors needed" to "four", and emitting
  // that would put a 07:00 row at the top of a box whose whole subject is what
  // changes LATER in the day. The opening ramp is not a step.
  const all = [];
  let prevRequired = candidates.length ? readAt(candidates[0]).required : 0;
  for (const T of candidates.slice(1)) {
    const read = readAt(T);
    if (read.required === prevRequired) continue;
    all.push({
      ...read,
      min: T,
      timeLabel: minutesToHm(T),
      time12h: to12h(T),
      direction: read.required < prevRequired ? "down" : "up",
    });
    prevRequired = read.required;
  }

  // The hold, resolved backwards: a step must keep every doctor that any LATER
  // step still needs. Walking from the end means each step reads one number
  // rather than re-scanning the tail, and — more to the point — it cannot
  // accidentally consider only the next step.
  let peakAfter = -Infinity;
  let peakStep = null;
  for (let i = all.length - 1; i >= 0; i--) {
    const step = all[i];
    // ⚠️ A CLIMB NEVER HOLDS. "Keep an extra doctor here" is meaningless at a
    // moment the board needs MORE of them, and the arithmetic happily produces
    // one: live on 2026-09-06 the 07:30 opening read "+2 · back to 4 MD at
    // 12:00". The hold is advice about a RELEASE, so only a release carries it.
    const holdExtra =
      step.direction === "down" && peakAfter > step.required ? peakAfter - step.required : 0;
    step.holdExtra = holdExtra;
    step.returnsTo = holdExtra ? peakAfter : null;
    step.returnsAtMin = holdExtra ? peakStep.min : null;
    step.returnsAtLabel = holdExtra ? peakStep.timeLabel : "";
    step.returnsAt12h = holdExtra ? peakStep.time12h : "";
    step.gapMin = holdExtra ? peakStep.min - step.min : null;
    if (step.required > peakAfter) {
      peakAfter = step.required;
      peakStep = step;
    }
  }

  // A step already behind us is history, not a plan. With no clock nothing is
  // filtered — a board that is not today shows its whole projection.
  const future = all
    .filter((s) => nowMin == null || s.min >= nowMin)
    .map((s) => ({
      ...s,
      // ⚠️ NEVER IMMINENT WITHOUT A CLOCK. `null` arithmetic would make every
      // step on a past board pulse for attention it cannot possibly need.
      imminent: nowMin != null && s.min - nowMin >= 0 && s.min - nowMin < warnMin,
      minutesAway: nowMin != null ? s.min - nowMin : null,
    }));

  // ⚠️ WHAT IS WORTH A ROW. The box answers "when do we come down in doctors",
  // and on a real board the morning ramp buries that answer: 2026-09-06 opened
  // rooms at 07:30, 08:00 and 09:00, three climbs that filled the box and
  // pushed every afternoon step-down behind a "+4 more later".
  //
  // So a climb earns a row only when it leaves you SHORT — the one climb
  // anybody has to act on. Every other climb is already described where it
  // matters: as the `↳ back to N MD at HH:MM` note on the release it threatens.
  //
  // The FULL future list survives as `future` because the peak below must see
  // the suppressed climbs. Filtering before measuring would release doctors the
  // evening still needs.
  const steps = future.filter((s) => s.direction === "down" || s.short > 0);

  const now = nowMin == null ? null : readAt(nowMin);

  // ⚠️ THE ONLY NUMBER ANYONE SHOULD ACT ON. `now.delta` is the spare RIGHT
  // NOW, and acting on it is exactly the mistake this module exists to stop:
  // two doctors spare at 16:00 is not two doctors who can go home if 18:00
  // needs one of them back. The releasable count is measured against the PEAK
  // of the rest of the day, so it is the number of people who are genuinely
  // finished — never a snapshot that expires an hour later.
  const peakAhead = future.reduce((m, s2) => Math.max(m, s2.required), now ? now.required : 0);
  const releasable = now ? Math.max(0, now.available - peakAhead) : null;

  // ⚠️ THE PEAK IS FOUND IN `future`, NEVER IN `steps`. `steps` is the filtered
  // list — a climb is dropped from it unless it leaves you short — and the climb
  // that holds a doctor back is, by definition, one the box chose not to show.
  // Looking the peak up in `steps` would come back empty for exactly the case
  // this number exists to explain. Same rule as the peak itself: measure
  // against the full future, present the filtered one.
  const peakStepAhead = future.find((s2) => s2.required === peakAhead) || null;

  return {
    now,
    steps,
    peakAhead: now ? peakAhead : null,
    // When the peak is merely today's current requirement, nothing ahead beats
    // it and there is no moment to name — the caller must degrade, not print
    // "at undefined".
    peakAtMin: peakStepAhead ? peakStepAhead.min : null,
    peakAtLabel: peakStepAhead ? peakStepAhead.timeLabel : "",
    releasable,
  };
}
